import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Smartphone, 
  QrCode, 
  Share2, 
  Download, 
  Eye, 
  Loader2,
  Sparkles,
  ExternalLink,
  Copy,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';

interface ARExperience {
  id: string;
  qr_code_url: string;
  ar_viewer_url: string;
  share_url: string;
  created_at: string;
}

export const ARAdCreator: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const modelUrl = searchParams.get('modelUrl');
  const title = searchParams.get('title') || "3D Model";

  const [isGenerating, setIsGenerating] = useState(false);
  const [arExperience, setArExperience] = useState<ARExperience | null>(null);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!modelUrl) {
      toast({
        title: "Missing 3D Model",
        description: "No 3D model URL provided for AR experience generation.",
        variant: "destructive",
      });
    }
  }, [modelUrl, toast]);

  const handleGenerateAR = async () => {
    if (!modelUrl) {
      toast({
        title: "No 3D Model",
        description: "A 3D model is required to generate an AR experience.",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    
    try {
      const response = await fetch('/api/v1/ar/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          model_url: modelUrl,
          title: title
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate AR experience');
      }

      const result = await response.json();
      setArExperience(result);
      
      toast({
        title: "AR Experience Created!",
        description: "Your AR visual is ready to share.",
      });
      
    } catch (error) {
      console.error('Error generating AR experience:', error);
      
      // For demo purposes, create a mock AR experience
      const mockExperience: ARExperience = {
        id: `ar_${Date.now()}`,
        qr_code_url: `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`https://ar-viewer.example.com/view/${Date.now()}`)}`,
        ar_viewer_url: `https://ar-viewer.example.com/view/${Date.now()}`,
        share_url: `https://quixocial.com/ar/${Date.now()}`,
        created_at: new Date().toISOString()
      };
      
      setArExperience(mockExperience);
      
      toast({
        title: "AR Experience Created!",
        description: "Your AR visual is ready to share. (Demo mode)",
      });
      
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    if (!arExperience) return;
    
    try {
      await navigator.clipboard.writeText(arExperience.share_url);
      setCopied(true);
      toast({
        title: "Link Copied!",
        description: "AR experience link copied to clipboard.",
      });
      
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Copy Failed",
        description: "Failed to copy link to clipboard.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadQR = () => {
    if (!arExperience) return;
    
    const link = document.createElement('a');
    link.href = arExperience.qr_code_url;
    link.download = `${title.replace(/\s+/g, '_')}_AR_QR.png`;
    link.click();
  };

  if (!modelUrl) {
    return (
      <Card className="w-full max-w-2xl mx-auto mt-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertDescription className="text-red-500">Error</AlertDescription>
          </CardTitle>
          <CardDescription>
            No 3D model URL found. Please go back and try again.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => navigate('/app/ai?tab=tasks')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Go Back
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!arExperience) {
    return (
      <Card className="w-full max-w-2xl mx-auto mt-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Create AR content
          </CardTitle>
          <CardDescription>
            Generate an interactive AR experience from your 3D model
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Preview */}
            <div className="text-center">
              <div className="w-32 h-32 mx-auto bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex items-center justify-center mb-4">
                <Smartphone className="h-16 w-16 text-purple-500" />
              </div>
              <h3 className="font-medium mb-2">AR Experience Preview</h3>
              <p className="text-sm text-muted-foreground">
                Your 3D model will be viewable in augmented reality on mobile devices
              </p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <h4 className="font-medium text-sm">What you'll get:</h4>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li className="flex items-center gap-2">
                    <QrCode className="h-4 w-4" />
                    QR code for easy access
                  </li>
                  <li className="flex items-center gap-2">
                    <Share2 className="h-4 w-4" />
                    Shareable AR viewer link
                  </li>
                  <li className="flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    Mobile AR experience
                  </li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Perfect for:</h4>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• Product demonstrations</li>
                  <li>• Interactive marketing</li>
                  <li>• Social media campaigns</li>
                  <li>• Trade show presentations</li>
                </ul>
              </div>
            </div>

            {/* Requirements */}
            <Alert>
              <Smartphone className="h-4 w-4" />
              <AlertDescription>
                <strong>Requirements:</strong> Users will need a smartphone with AR capabilities (iOS 11+ or Android 7.0+) to view the AR experience.
              </AlertDescription>
            </Alert>

            {/* Generate Button */}
            <Button 
              onClick={handleGenerateAR}
              disabled={isGenerating || !modelUrl}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating AR Experience...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate AR content
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-2xl mx-auto mt-8">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              AR Experience Ready!
            </CardTitle>
            <CardDescription>
              Your AR content has been created successfully
            </CardDescription>
          </div>
          <Badge className="bg-green-100 text-green-800">
            Active
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* QR Code and Preview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="text-center">
              <h3 className="font-medium mb-3">QR Code</h3>
              <div className="bg-white p-4 rounded-lg border inline-block">
                <img 
                  src={arExperience.qr_code_url} 
                  alt="AR Experience QR Code"
                  className="w-32 h-32"
                />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Scan with smartphone camera
              </p>
            </div>
            
            <div className="text-center">
              <h3 className="font-medium mb-3">AR Preview</h3>
              <div className="w-32 h-32 mx-auto bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center">
                <Smartphone className="h-16 w-16 text-blue-500" />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Interactive 3D model in AR
              </p>
            </div>
          </div>

          {/* Share Options */}
          <div className="space-y-4">
            <h3 className="font-medium">Share Your AR Experience</h3>
            
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <input 
                type="text" 
                value={arExperience.share_url}
                readOnly
                className="flex-1 bg-transparent text-sm"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyLink}
                className="shrink-0"
              >
                {copied ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={handleDownloadQR}
              className="flex-1"
            >
              <Download className="h-4 w-4 mr-2" />
              Download QR Code
            </Button>
            
            <Button
              variant="outline"
              onClick={() => window.open(arExperience.ar_viewer_url, '_blank')}
              className="flex-1"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Preview AR
            </Button>
            
            <Button
              onClick={handleCopyLink}
              className="flex-1"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share Link
            </Button>
          </div>

          {/* Instructions */}
          <Alert>
            <Smartphone className="h-4 w-4" />
            <AlertDescription>
              <strong>How to use:</strong> Share the QR code or link with your audience. They can scan the QR code or visit the link on their smartphone to view your 3D model in augmented reality.
            </AlertDescription>
          </Alert>

          {/* Close Button */}
          <div className="mt-4 text-center">
            <Button onClick={() => window.history.back()} className="w-full">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Go Back
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};